<template>
  <div>
    <b-modal
      hide-footer
      hide-backdrop
      no-close-on-esc
      title="Print Settings"
      ref="posPrintSettingsModal"
      id="posPrintSettingsModal"
      content-class="pps-modal"
      body-class="pps-modal-body"
      header-class="pps-modal-header"
      size="md"
      centered
      @shown="onModalShown"
      @hidden="closeKeyboards"
    >
      <div class="pps-layout">
        <div class="pps-row">
          <label class="pps-label" for="ppsTicketTemplate">Ticket Template</label>
          <div class="pps-select-wrap" v-if="!isLoadingTemplates">
            <select
              id="ppsTicketTemplate"
              class="pps-select"
              :value="ticketDropdownValue"
              @change="onTicketTemplateChange"
            >
              <option value="">Cinema Default</option>
              <option
                v-for="tpl in templateOptions"
                :key="'ticket-' + tpl.key"
                :value="String(tpl.template_id)"
              >
                {{ tpl.label }} (ID {{ tpl.template_id }})
              </option>
            </select>
            <span class="pps-select-caret" aria-hidden="true"></span>
          </div>
          <div class="pps-status" v-else>Loading…</div>
        </div>

        <div class="pps-row">
          <label class="pps-label" for="ppsTicketPrinter">Ticket Printer</label>
          <div class="pps-input-row">
            <input
              id="ppsTicketPrinter"
              v-model="ticketPrinterName"
              type="text"
              class="pps-input"
              placeholder="Printer name (blank = default)"
              autocomplete="off"
              spellcheck="false"
            />
            <div
              class="keyboard-icon pps-kb-icon"
              @click.prevent.stop="toggleKeyboard('ticket')"
            ></div>
          </div>
          <Vkeyboard
            :inputName="'ppsTicketPrinter'"
            :showOrHide="keyboardsVisible.ticket"
            :initialValue="ticketPrinterName"
            @changeKeyboardInput="ticketPrinterName = $event"
            @onClickClose="keyboardsVisible.ticket = false"
          />
        </div>

        <div class="pps-divider"></div>

        <div class="pps-row">
          <label class="pps-label" for="ppsReceiptTemplate"
            >Receipt Template</label
          >
          <div class="pps-select-wrap" v-if="!isLoadingTemplates">
            <select
              id="ppsReceiptTemplate"
              class="pps-select"
              :value="receiptDropdownValue"
              @change="onReceiptTemplateChange"
            >
              <option value="">Cinema Default</option>
              <option
                v-for="tpl in templateOptions"
                :key="'receipt-' + tpl.key"
                :value="String(tpl.template_id)"
              >
                {{ tpl.label }} (ID {{ tpl.template_id }})
              </option>
            </select>
            <span class="pps-select-caret" aria-hidden="true"></span>
          </div>
          <div class="pps-status" v-else>Loading…</div>
        </div>

        <div class="pps-row">
          <label class="pps-label" for="ppsReceiptPrinter">Receipt Printer</label>
          <div class="pps-input-row">
            <input
              id="ppsReceiptPrinter"
              v-model="receiptPrinterName"
              type="text"
              class="pps-input"
              placeholder="Printer name (blank = default)"
              autocomplete="off"
              spellcheck="false"
            />
            <div
              class="keyboard-icon pps-kb-icon"
              @click.prevent.stop="toggleKeyboard('receipt')"
            ></div>
          </div>
          <Vkeyboard
            :inputName="'ppsReceiptPrinter'"
            :showOrHide="keyboardsVisible.receipt"
            :initialValue="receiptPrinterName"
            @changeKeyboardInput="receiptPrinterName = $event"
            @onClickClose="keyboardsVisible.receipt = false"
          />
        </div>

        <p class="pps-note">
          If receipt is attached with ticket, add only one.
        </p>
      </div>

      <div class="pps-actions">
        <button
          type="button"
          class="pps-btn-secondary"
          :disabled="isSaving"
          @click.prevent.stop="$bvModal.hide('posPrintSettingsModal')"
        >
          Cancel
        </button>
        <button
          class="btn-blue pps-btn-primary"
          :disabled="isSaving || isLoadingTemplates"
          @click="saveSettings"
        >
          {{ isSaving ? "Saving…" : "Save" }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import MasterService from "@/services/MasterService";
import Vkeyboard from "@/components/Partials/Vkeyboard.vue";
import {
  getPrintSettings,
  savePrintSettings,
} from "@/helper/posPrintSettings";

export default {
  components: {
    Vkeyboard,
  },
  data() {
    return {
      isSaving: false,
      isLoadingTemplates: false,
      templateOptions: [],
      selectedTemplateId: null,
      selectedTemplateName: "",
      selectedTtId: null,
      selectedReceiptTemplateId: null,
      selectedReceiptTemplateName: "",
      selectedReceiptTtId: null,
      ticketPrinterName: "",
      receiptPrinterName: "",
      keyboardsVisible: {
        ticket: false,
        receipt: false,
      },
    };
  },
  computed: {
    ticketDropdownValue() {
      return this.selectedTemplateId != null && this.selectedTemplateId !== ""
        ? String(this.selectedTemplateId)
        : "";
    },
    receiptDropdownValue() {
      return this.selectedReceiptTemplateId != null &&
        this.selectedReceiptTemplateId !== ""
        ? String(this.selectedReceiptTemplateId)
        : "";
    },
  },
  methods: {
    toggleKeyboard(type) {
      const next = !this.keyboardsVisible[type];
      this.keyboardsVisible.ticket = false;
      this.keyboardsVisible.receipt = false;
      this.keyboardsVisible[type] = next;
    },
    closeKeyboards() {
      this.keyboardsVisible.ticket = false;
      this.keyboardsVisible.receipt = false;
    },
    getCinemaId() {
      return (
        window.localStorage.getItem("cinema_id") ||
        (this.UsersDetails && this.UsersDetails.cinema_id) ||
        ""
      );
    },
    onTicketTemplateChange(event) {
      const value = event && event.target ? event.target.value : "";
      if (!value) {
        this.selectTicketTemplate(null);
        return;
      }
      const matched = this.templateOptions.find(
        (opt) => String(opt.template_id) === String(value)
      );
      this.selectTicketTemplate(matched || null);
    },
    onReceiptTemplateChange(event) {
      const value = event && event.target ? event.target.value : "";
      if (!value) {
        this.selectReceiptTemplate(null);
        return;
      }
      const matched = this.templateOptions.find(
        (opt) => String(opt.template_id) === String(value)
      );
      this.selectReceiptTemplate(matched || null);
    },
    selectTicketTemplate(tpl) {
      if (!tpl) {
        this.selectedTemplateId = null;
        this.selectedTemplateName = "";
        this.selectedTtId = null;
        return;
      }
      this.selectedTemplateId = tpl.template_id;
      this.selectedTemplateName = tpl.label;
      this.selectedTtId = tpl.tt_id != null ? tpl.tt_id : null;
    },
    selectReceiptTemplate(tpl) {
      if (!tpl) {
        this.selectedReceiptTemplateId = null;
        this.selectedReceiptTemplateName = "";
        this.selectedReceiptTtId = null;
        return;
      }
      this.selectedReceiptTemplateId = tpl.template_id;
      this.selectedReceiptTemplateName = tpl.label;
      this.selectedReceiptTtId = tpl.tt_id != null ? tpl.tt_id : null;
    },
    matchTemplateMeta(templateId) {
      if (templateId == null || templateId === "") return null;
      return (
        this.templateOptions.find(
          (opt) => String(opt.template_id) === String(templateId)
        ) || null
      );
    },
    async syncFromStorage() {
      const saved = await getPrintSettings();
      this.ticketPrinterName = saved.ticket_printer_name || "";
      this.receiptPrinterName = saved.receipt_printer_name || "";
      this.selectedTemplateId =
        saved.template_id != null && saved.template_id !== ""
          ? saved.template_id
          : null;
      this.selectedTemplateName = saved.template_name || "";
      this.selectedTtId = saved.tt_id != null ? saved.tt_id : null;
      this.selectedReceiptTemplateId =
        saved.receipt_template_id != null && saved.receipt_template_id !== ""
          ? saved.receipt_template_id
          : null;
      this.selectedReceiptTemplateName = saved.receipt_template_name || "";
      this.selectedReceiptTtId =
        saved.receipt_tt_id != null ? saved.receipt_tt_id : null;

      const ticketMatched = this.matchTemplateMeta(this.selectedTemplateId);
      if (ticketMatched) {
        this.selectedTemplateName = ticketMatched.label;
        this.selectedTtId =
          ticketMatched.tt_id != null
            ? ticketMatched.tt_id
            : this.selectedTtId;
      }

      const receiptMatched = this.matchTemplateMeta(
        this.selectedReceiptTemplateId
      );
      if (receiptMatched) {
        this.selectedReceiptTemplateName = receiptMatched.label;
        this.selectedReceiptTtId =
          receiptMatched.tt_id != null
            ? receiptMatched.tt_id
            : this.selectedReceiptTtId;
      }
    },
    normalizeTemplates(records) {
      if (!Array.isArray(records)) return [];
      const seen = {};
      const list = [];
      records.forEach((item, index) => {
        if (!item || typeof item !== "object") return;
        const template_id =
          item.template_id != null
            ? item.template_id
            : item.t_id != null
            ? item.t_id
            : null;
        if (template_id == null) return;
        const key = String(template_id);
        if (seen[key]) return;
        seen[key] = true;
        const label =
          item.t_name ||
          item.template_name ||
          item.name ||
          item.label ||
          `Template ${template_id}`;
        list.push({
          key: `${key}-${item.tt_id || index}`,
          template_id,
          tt_id: item.tt_id != null ? item.tt_id : null,
          label,
        });
      });
      return list;
    },
    async loadTemplates() {
      this.isLoadingTemplates = true;
      const cinemaId = this.getCinemaId();
      let options = [];

      try {
        if (cinemaId) {
          const cinemaRes = await MasterService.TicketTemplateByCinemaID({
            cine_id: cinemaId,
          });
          const cinemaData = cinemaRes && cinemaRes.data;
          if (
            cinemaData &&
            cinemaData.status &&
            cinemaData.Records &&
            cinemaData.Records.length
          ) {
            options = this.normalizeTemplates(cinemaData.Records);
          }
        }
      } catch (err) {
        console.log("TicketTemplateByCinemaID error", err);
      }

      try {
        const allRes = await MasterService.GetTicketTemplate({
          limit: 200,
          currentPage: 1,
        });
        const allData = allRes && allRes.data;
        if (
          allData &&
          allData.status &&
          allData.Records &&
          allData.Records.length
        ) {
          const masterList = this.normalizeTemplates(
            allData.Records.map((row) => ({
              ...row,
              template_id: row.t_id,
              t_name: row.t_name,
            }))
          );
          const existing = {};
          options.forEach((o) => {
            existing[String(o.template_id)] = true;
          });
          masterList.forEach((o) => {
            if (!existing[String(o.template_id)]) {
              options.push(o);
            }
          });
        }
      } catch (err) {
        console.log("GetTicketTemplate error", err);
      }

      try {
        if (cinemaId) {
          const mapRes = await MasterService.GetCinemaTicketTemplate({
            limit: 200,
            currentPage: 1,
            cinema_id: cinemaId,
            columnName: "tt_id",
            orderBy: "asc",
          });
          const mapData = mapRes && mapRes.data;
          if (
            mapData &&
            mapData.status &&
            mapData.Records &&
            mapData.Records.length
          ) {
            const mapped = this.normalizeTemplates(mapData.Records);
            const existing = {};
            options.forEach((o) => {
              existing[String(o.template_id)] = true;
            });
            mapped.forEach((o) => {
              if (!existing[String(o.template_id)]) {
                options.push(o);
              }
            });
          }
        }
      } catch (err) {
        console.log("GetCinemaTicketTemplate error", err);
      }

      this.templateOptions = options;
      this.isLoadingTemplates = false;
      await this.syncFromStorage();
    },
    async onModalShown() {
      await this.loadTemplates();
    },
    async saveSettings() {
      try {
        this.isSaving = true;
        this.closeKeyboards();
        await savePrintSettings({
          template_id: this.selectedTemplateId,
          template_name: this.selectedTemplateName || "",
          tt_id: this.selectedTtId,
          receipt_template_id: this.selectedReceiptTemplateId,
          receipt_template_name: this.selectedReceiptTemplateName || "",
          receipt_tt_id: this.selectedReceiptTtId,
          ticket_printer_name: (this.ticketPrinterName || "").trim(),
          receipt_printer_name: (this.receiptPrinterName || "").trim(),
        });
        this.$toasted.show("Print settings saved.");
        this.$bvModal.hide("posPrintSettingsModal");
      } catch (err) {
        console.log("Unable to save print settings", err);
        this.$toasted.show("Unable to save settings.");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style>
#posPrintSettingsModal___BV_modal_content_ {
  width: 440px !important;
  max-width: 92vw;
  margin: auto;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);
}

#posPrintSettingsModal___BV_modal_header_ {
  border-bottom: 1px solid #e8e8e8;
  padding: 12px 16px 10px;
}

#posPrintSettingsModal___BV_modal_title_ {
  width: 100%;
  text-align: left;
  color: var(--blue);
  font-weight: 700;
  font-size: 16px;
  margin: 0;
}

.pps-modal-body {
  padding: 12px 16px 14px !important;
}

.pps-layout {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pps-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pps-label {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #555;
}

.pps-divider {
  height: 1px;
  background: #ececec;
  margin: 2px 0;
}

.pps-select-wrap {
  position: relative;
  width: 100%;
}

.pps-select,
.pps-input {
  width: 100%;
  height: 36px;
  border: 1.5px solid #d5d5d5;
  border-radius: 6px;
  padding: 0 12px;
  font-size: 13px;
  color: #222;
  background: #fff;
  outline: none;
}

.pps-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pps-input-row .pps-input {
  flex: 1;
  min-width: 0;
}

.pps-kb-icon {
  flex: 0 0 auto;
  cursor: pointer;
  width: 26px;
  height: 26px;
}

.pps-select {
  padding-right: 34px;
  font-weight: 600;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
}

.pps-select:focus,
.pps-input:focus {
  border-color: var(--blue);
}

.pps-select-caret {
  pointer-events: none;
  position: absolute;
  right: 12px;
  top: 50%;
  width: 8px;
  height: 8px;
  margin-top: -5px;
  border-right: 2px solid #777;
  border-bottom: 2px solid #777;
  transform: rotate(45deg);
}

.pps-input::placeholder {
  color: #999;
  font-weight: 400;
}

.pps-status {
  height: 36px;
  display: flex;
  align-items: center;
  color: #777;
  font-size: 12px;
}

.pps-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  margin-top: 12px;
}

.pps-note {
  margin: 2px 0 0;
  font-size: 11px;
  line-height: 1.35;
  color: #888;
}

.pps-btn-primary {
  min-width: 84px;
}

.pps-btn-secondary {
  min-width: 76px;
  height: 34px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1.5px solid #c8c8c8;
  background: #fff;
  color: #444;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}

.pps-btn-secondary:hover:not(:disabled) {
  border-color: #999;
  background: #f5f5f5;
}

.pps-btn-secondary:disabled,
.pps-btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/components/Partials/PosPrintSettingsPopup.vue